import { BaseLabelJSON } from "../../../constants/exportsCountries"
import { DOMESTIC_NUTRITION_KEYS } from "../../../Utils/constants"

export const GCC_NUTRITION_KEYS = {
  ...DOMESTIC_NUTRITION_KEYS,
  DIETARY_FIBRE: "dietary_fiber",
}

export type GCCMandatoryNutrient =
  | typeof GCC_NUTRITION_KEYS.ENERGY
  | typeof GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE
  | typeof GCC_NUTRITION_KEYS.TOTAL_SUGARS
  | typeof GCC_NUTRITION_KEYS.ADDED_SUGARS
  | typeof GCC_NUTRITION_KEYS.DIETARY_FIBRE
  | typeof GCC_NUTRITION_KEYS.PROTEIN
  | typeof GCC_NUTRITION_KEYS.TOTAL_FAT
  | typeof GCC_NUTRITION_KEYS.SATURATED_FAT
  | typeof GCC_NUTRITION_KEYS.SODIUM
  | typeof GCC_NUTRITION_KEYS.CHOLESTEROL
  | typeof GCC_NUTRITION_KEYS.TRANS_FAT

export type GCCMandatoryNutrientObject = Record<GCCMandatoryNutrient, string>

export interface GCCLabelJson extends BaseLabelJSON {
  mandatory_nutrient: [GCCMandatoryNutrientObject]
  translated_household_quantity: string
}

export const GCC_NO_DV_NUTRIENT_KEYS = [
  GCC_NUTRITION_KEYS.TRANS_FAT,
  GCC_NUTRITION_KEYS.TOTAL_SUGARS,
  GCC_NUTRITION_KEYS.PROTEIN,
  GCC_NUTRITION_KEYS.MUFA,
  GCC_NUTRITION_KEYS.PUFA,
  GCC_NUTRITION_KEYS.POLYOLS,
  GCC_NUTRITION_KEYS.STARCH,
  GCC_NUTRITION_KEYS.CHROMIUM,
  GCC_NUTRITION_KEYS.CHOLINE,
]
export type GCCNonMandatoryInterleavedNutrient =
  | typeof GCC_NUTRITION_KEYS.PUFA
  | typeof GCC_NUTRITION_KEYS.MUFA
  | typeof GCC_NUTRITION_KEYS.POLYOLS
  | typeof GCC_NUTRITION_KEYS.STARCH

export const GCC_INTERLEAVED_NUTRIENTS: GCCNonMandatoryInterleavedNutrient[] = [
  GCC_NUTRITION_KEYS.PUFA,
  GCC_NUTRITION_KEYS.MUFA,
  GCC_NUTRITION_KEYS.POLYOLS,
  GCC_NUTRITION_KEYS.STARCH,
]

export type GCCNutrientData = {
  key: string
  unit: string
  name: string
  per_serve: string | number
  daily_value: string | number
}

export type GCCInterLeavedNonMandatoryNutrientData = Partial<{
  MONOUNSATURATED_FATTY_ACIDS: GCCNutrientData
  POLYUNSATURATED_FATTY_ACIDS: GCCNutrientData
  POLYOLS: GCCNutrientData
  STARCH: GCCNutrientData
}>

export type CanadaMandatoryNutrientData = {
  TOTAL_FAT: GCCNutrientData
  SATURATED_FAT: GCCNutrientData
  TRANS_FAT: GCCNutrientData
  CHOLESTEROL: GCCNutrientData
  SODIUM: GCCNutrientData
  TOTAL_CARBOHYDRATES: GCCNutrientData
  DIETARY_FIBER: GCCNutrientData
  TOTAL_SUGARS: GCCNutrientData
  INCLUDES_ADDED_SUGARS: GCCNutrientData
  PROTEIN: GCCNutrientData
}

export type GCCNutritionTableValue = {
  serving_per_container: number | string
  serving_size: number | string
  serving_size_unit: string
  household_quantity: string
  calories: string | number
  mandatory_nutrient: CanadaMandatoryNutrientData
  non_mandatory_nutrient: GCCNutrientData[]
  interleaved_nutrient: GCCInterLeavedNonMandatoryNutrientData
}

export const GCC_NON_MANDATORY_NUTRIENT_SEQUENCE = [
  GCC_NUTRITION_KEYS.IRON,
  GCC_NUTRITION_KEYS.CALCIUM,
  GCC_NUTRITION_KEYS.FOLIC_ACID,
  GCC_NUTRITION_KEYS.IODINE,
  GCC_NUTRITION_KEYS.VITAMIN_B6,
  GCC_NUTRITION_KEYS.VITAMIN_B12,
  GCC_NUTRITION_KEYS.VITAMIN_C,
  GCC_NUTRITION_KEYS.ZINC,
  GCC_NUTRITION_KEYS.MAGNESIUM,
  GCC_NUTRITION_KEYS.VITAMIN_D,
  GCC_NUTRITION_KEYS.VITAMIN_K1,
  GCC_NUTRITION_KEYS.VITAMIN_B1, // Thiamin
  GCC_NUTRITION_KEYS.VITAMIN_B2, // Riboflavin
  GCC_NUTRITION_KEYS.BIOTIN,
  GCC_NUTRITION_KEYS.VITAMIN_B3, // Niacin
  GCC_NUTRITION_KEYS.PANTOTHENIC_ACID,
  GCC_NUTRITION_KEYS.VITAMIN_E,
  GCC_NUTRITION_KEYS.VITAMIN_A,
  GCC_NUTRITION_KEYS.SELENIUM,
  GCC_NUTRITION_KEYS.COPPER,
  GCC_NUTRITION_KEYS.POTASSIUM,
  GCC_NUTRITION_KEYS.CHLORIDE,
  GCC_NUTRITION_KEYS.MANGANESE,
  GCC_NUTRITION_KEYS.PHOSPHORUS,
  GCC_NUTRITION_KEYS.CHROMIUM,
  GCC_NUTRITION_KEYS.CHOLINE,
  GCC_NUTRITION_KEYS.MOLYBDENUM,
]

export const GCC_DV_VALUES = {
  "1-3 years": {
    [GCC_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: 2000,
      unit: "kcal",
    },
    [GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total carbohydrates / الكربوهيدرات الكلية",
      daily_value: 260,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total sugars / السكريات الكلية",
      daily_value: 90,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Contains ___ g added sugar / يتضمن ____ غرام سكر مضاف",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / بروتين",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total fat / الدهون الكلية",
      daily_value: 70,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated fat / الدهون المشبعة",
      daily_value: 20,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans fat / الدهون المتحولة",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium / الصوديوم",
      daily_value: 2000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / الكوليسترول ",
      daily_value: 300,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary fiber / الألياف الغذائية",
      daily_value: 28,
      unit: "g",
    },

    [GCC_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron /حديد",
      daily_value: 22,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium / كالسيوم",
      daily_value: 1000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folic Acid (Folate) / حمض الفوليك (فولات)",
      daily_value: 400,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine / اليود",
      daily_value: 150,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6 / فيتامين (ب6)",
      daily_value: 1.3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12 / فيتامين (ب12)",
      daily_value: 2.4,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / فيتامين (ج) أو فيتامين سي",
      daily_value: 100,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc / زنك",
      daily_value: 14,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / ماغنيسيوم",
      daily_value: 310,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / فيتامين (د)",
      daily_value: 15,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / فيتامين (ك)",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamin / الثيامين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / الريبوفلافين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / بيوتين",
      daily_value: 30,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / نياسين",
      daily_value: 15,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid / حمض بانتوثينيك",
      daily_value: 5,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / فيتامين (هـ) أو فيتامين إي",
      daily_value: 9,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / فيتامين (أ)",
      daily_value: 800,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / سيلينيوم",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / نحاس",
      daily_value: 900,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium / البوتاسيوم",
      daily_value: 3500,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / كلوريد",
      daily_value: null,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / منجنيز",
      daily_value: 3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus / فسفور",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / كروميوم",
      daily_value: 700,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline / كولين",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / موليبدينوم",
      daily_value: 45,
      unit: "µg",
    },
  },
  "4-6 years": {
    [GCC_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: 2000,
      unit: "kcal",
    },
    [GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total carbohydrates / الكربوهيدرات الكلية",
      daily_value: 260,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total sugars / السكريات الكلية",
      daily_value: 90,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Contains ___ g added sugar / يتضمن ____ غرام سكر مضاف",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / بروتين",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total fat / الدهون الكلية",
      daily_value: 70,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated fat / الدهون المشبعة",
      daily_value: 20,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans fat / الدهون المتحولة",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium / الصوديوم",
      daily_value: 2000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / الكوليسترول ",
      daily_value: 300,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary fiber / الألياف الغذائية",
      daily_value: 28,
      unit: "g",
    },

    [GCC_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron /حديد",
      daily_value: 22,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium / كالسيوم",
      daily_value: 1000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folic Acid (Folate) / حمض الفوليك (فولات)",
      daily_value: 400,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine / اليود",
      daily_value: 150,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6 / فيتامين (ب6)",
      daily_value: 1.3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12 / فيتامين (ب12)",
      daily_value: 2.4,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / فيتامين (ج) أو فيتامين سي",
      daily_value: 100,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc / زنك",
      daily_value: 14,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / ماغنيسيوم",
      daily_value: 310,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / فيتامين (د)",
      daily_value: 15,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / فيتامين (ك)",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamin / الثيامين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / الريبوفلافين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / بيوتين",
      daily_value: 30,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / نياسين",
      daily_value: 15,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid / حمض بانتوثينيك",
      daily_value: 5,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / فيتامين (هـ) أو فيتامين إي",
      daily_value: 9,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / فيتامين (أ)",
      daily_value: 800,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / سيلينيوم",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / نحاس",
      daily_value: 900,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium / البوتاسيوم",
      daily_value: 3500,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / كلوريد",
      daily_value: null,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / منجنيز",
      daily_value: 3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus / فسفور",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / كروميوم",
      daily_value: 700,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline / كولين",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / موليبدينوم",
      daily_value: 45,
      unit: "µg",
    },
  },
  "7-9 years": {
    [GCC_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: 2000,
      unit: "kcal",
    },
    [GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total carbohydrates / الكربوهيدرات الكلية",
      daily_value: 260,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total sugars / السكريات الكلية",
      daily_value: 90,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Contains ___ g added sugar / يتضمن ____ غرام سكر مضاف",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / بروتين",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total fat / الدهون الكلية",
      daily_value: 70,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated fat / الدهون المشبعة",
      daily_value: 20,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans fat / الدهون المتحولة",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium / الصوديوم",
      daily_value: 2000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / الكوليسترول ",
      daily_value: 300,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary fiber / الألياف الغذائية",
      daily_value: 28,
      unit: "g",
    },

    [GCC_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron /حديد",
      daily_value: 22,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium / كالسيوم",
      daily_value: 1000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folic Acid (Folate) / حمض الفوليك (فولات)",
      daily_value: 400,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine / اليود",
      daily_value: 150,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6 / فيتامين (ب6)",
      daily_value: 1.3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12 / فيتامين (ب12)",
      daily_value: 2.4,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / فيتامين (ج) أو فيتامين سي",
      daily_value: 100,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc / زنك",
      daily_value: 14,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / ماغنيسيوم",
      daily_value: 310,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / فيتامين (د)",
      daily_value: 15,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / فيتامين (ك)",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamin / الثيامين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / الريبوفلافين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / بيوتين",
      daily_value: 30,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / نياسين",
      daily_value: 15,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid / حمض بانتوثينيك",
      daily_value: 5,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / فيتامين (هـ) أو فيتامين إي",
      daily_value: 9,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / فيتامين (أ)",
      daily_value: 800,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / سيلينيوم",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / نحاس",
      daily_value: 900,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium / البوتاسيوم",
      daily_value: 3500,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / كلوريد",
      daily_value: null,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / منجنيز",
      daily_value: 3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus / فسفور",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / كروميوم",
      daily_value: 700,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline / كولين",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / موليبدينوم",
      daily_value: 45,
      unit: "µg",
    },
  },
  "10-12 years": {
    [GCC_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: 2000,
      unit: "kcal",
    },
    [GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total carbohydrates / الكربوهيدرات الكلية",
      daily_value: 260,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total sugars / السكريات الكلية",
      daily_value: 90,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Contains ___ g added sugar / يتضمن ____ غرام سكر مضاف",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / بروتين",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total fat / الدهون الكلية",
      daily_value: 70,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated fat / الدهون المشبعة",
      daily_value: 20,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans fat / الدهون المتحولة",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium / الصوديوم",
      daily_value: 2000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / الكوليسترول ",
      daily_value: 300,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary fiber / الألياف الغذائية",
      daily_value: 28,
      unit: "g",
    },

    [GCC_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron /حديد",
      daily_value: 22,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium / كالسيوم",
      daily_value: 1000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folic Acid (Folate) / حمض الفوليك (فولات)",
      daily_value: 400,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine / اليود",
      daily_value: 150,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6 / فيتامين (ب6)",
      daily_value: 1.3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12 / فيتامين (ب12)",
      daily_value: 2.4,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / فيتامين (ج) أو فيتامين سي",
      daily_value: 100,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc / زنك",
      daily_value: 14,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / ماغنيسيوم",
      daily_value: 310,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / فيتامين (د)",
      daily_value: 15,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / فيتامين (ك)",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamin / الثيامين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / الريبوفلافين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / بيوتين",
      daily_value: 30,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / نياسين",
      daily_value: 15,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid / حمض بانتوثينيك",
      daily_value: 5,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / فيتامين (هـ) أو فيتامين إي",
      daily_value: 9,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / فيتامين (أ)",
      daily_value: 800,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / سيلينيوم",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / نحاس",
      daily_value: 900,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium / البوتاسيوم",
      daily_value: 3500,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / كلوريد",
      daily_value: null,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / منجنيز",
      daily_value: 3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus / فسفور",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / كروميوم",
      daily_value: 700,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline / كولين",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / موليبدينوم",
      daily_value: 45,
      unit: "µg",
    },
  },
  "13-15 years": {
    [GCC_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: 2000,
      unit: "kcal",
    },
    [GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total carbohydrates / الكربوهيدرات الكلية",
      daily_value: 260,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total sugars / السكريات الكلية",
      daily_value: 90,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Contains ___ g added sugar / يتضمن ____ غرام سكر مضاف",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / بروتين",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total fat / الدهون الكلية",
      daily_value: 70,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated fat / الدهون المشبعة",
      daily_value: 20,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans fat / الدهون المتحولة",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium / الصوديوم",
      daily_value: 2000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / الكوليسترول ",
      daily_value: 300,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary fiber / الألياف الغذائية",
      daily_value: 28,
      unit: "g",
    },

    [GCC_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron /حديد",
      daily_value: 22,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium / كالسيوم",
      daily_value: 1000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folic Acid (Folate) / حمض الفوليك (فولات)",
      daily_value: 400,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine / اليود",
      daily_value: 150,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6 / فيتامين (ب6)",
      daily_value: 1.3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12 / فيتامين (ب12)",
      daily_value: 2.4,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / فيتامين (ج) أو فيتامين سي",
      daily_value: 100,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc / زنك",
      daily_value: 14,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / ماغنيسيوم",
      daily_value: 310,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / فيتامين (د)",
      daily_value: 15,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / فيتامين (ك)",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamin / الثيامين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / الريبوفلافين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / بيوتين",
      daily_value: 30,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / نياسين",
      daily_value: 15,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid / حمض بانتوثينيك",
      daily_value: 5,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / فيتامين (هـ) أو فيتامين إي",
      daily_value: 9,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / فيتامين (أ)",
      daily_value: 800,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / سيلينيوم",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / نحاس",
      daily_value: 900,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium / البوتاسيوم",
      daily_value: 3500,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / كلوريد",
      daily_value: null,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / منجنيز",
      daily_value: 3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus / فسفور",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / كروميوم",
      daily_value: 700,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline / كولين",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / موليبدينوم",
      daily_value: 45,
      unit: "µg",
    },
  },
  "16-18 years": {
    [GCC_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: 2000,
      unit: "kcal",
    },
    [GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total carbohydrates / الكربوهيدرات الكلية",
      daily_value: 260,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total sugars / السكريات الكلية",
      daily_value: 90,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Contains ___ g added sugar / يتضمن ____ غرام سكر مضاف",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / بروتين",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total fat / الدهون الكلية",
      daily_value: 70,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated fat / الدهون المشبعة",
      daily_value: 20,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans fat / الدهون المتحولة",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium / الصوديوم",
      daily_value: 2000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / الكوليسترول ",
      daily_value: 300,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary fiber / الألياف الغذائية",
      daily_value: 28,
      unit: "g",
    },

    [GCC_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron /حديد",
      daily_value: 22,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium / كالسيوم",
      daily_value: 1000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folic Acid (Folate) / حمض الفوليك (فولات)",
      daily_value: 400,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine / اليود",
      daily_value: 150,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6 / فيتامين (ب6)",
      daily_value: 1.3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12 / فيتامين (ب12)",
      daily_value: 2.4,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / فيتامين (ج) أو فيتامين سي",
      daily_value: 100,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc / زنك",
      daily_value: 14,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / ماغنيسيوم",
      daily_value: 310,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / فيتامين (د)",
      daily_value: 15,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / فيتامين (ك)",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamin / الثيامين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / الريبوفلافين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / بيوتين",
      daily_value: 30,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / نياسين",
      daily_value: 15,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid / حمض بانتوثينيك",
      daily_value: 5,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / فيتامين (هـ) أو فيتامين إي",
      daily_value: 9,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / فيتامين (أ)",
      daily_value: 800,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / سيلينيوم",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / نحاس",
      daily_value: 900,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium / البوتاسيوم",
      daily_value: 3500,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / كلوريد",
      daily_value: null,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / منجنيز",
      daily_value: 3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus / فسفور",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / كروميوم",
      daily_value: 700,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline / كولين",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / موليبدينوم",
      daily_value: 45,
      unit: "µg",
    },
  },
  "Above 18 years": {
    [GCC_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: 2000,
      unit: "kcal",
    },
    [GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total carbohydrates / الكربوهيدرات الكلية",
      daily_value: 260,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total sugars / السكريات الكلية",
      daily_value: 90,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Contains ___ g added sugar / يتضمن ____ غرام سكر مضاف",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / بروتين",
      daily_value: 50,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total fat / الدهون الكلية",
      daily_value: 70,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated fat / الدهون المشبعة",
      daily_value: 20,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans fat / الدهون المتحولة",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium / الصوديوم",
      daily_value: 2000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / الكوليسترول ",
      daily_value: 300,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary fiber / الألياف الغذائية",
      daily_value: 28,
      unit: "g",
    },

    [GCC_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron /حديد",
      daily_value: 22,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium / كالسيوم",
      daily_value: 1000,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folic Acid (Folate) / حمض الفوليك (فولات)",
      daily_value: 400,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine / اليود",
      daily_value: 150,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6 / فيتامين (ب6)",
      daily_value: 1.3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12 / فيتامين (ب12)",
      daily_value: 2.4,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / فيتامين (ج) أو فيتامين سي",
      daily_value: 100,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc / زنك",
      daily_value: 14,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / ماغنيسيوم",
      daily_value: 310,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / فيتامين (د)",
      daily_value: 15,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / فيتامين (ك)",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamin / الثيامين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / الريبوفلافين",
      daily_value: 1.2,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / بيوتين",
      daily_value: 30,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / نياسين",
      daily_value: 15,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid / حمض بانتوثينيك",
      daily_value: 5,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / فيتامين (هـ) أو فيتامين إي",
      daily_value: 9,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / فيتامين (أ)",
      daily_value: 800,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / سيلينيوم",
      daily_value: 60,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / نحاس",
      daily_value: 900,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium / البوتاسيوم",
      daily_value: 3500,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / كلوريد",
      daily_value: null,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / منجنيز",
      daily_value: 3,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus / فسفور",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / كروميوم",
      daily_value: 700,
      unit: "µg",
    },
    [GCC_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline / كولين",
      daily_value: 700,
      unit: "mg",
    },
    [GCC_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids (g)",
      daily_value: null,
      unit: "g",
    },
    [GCC_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / موليبدينوم",
      daily_value: 45,
      unit: "µg",
    },
  },
}
